<script setup lang="ts">
// types
import type { BlockBannerTextBlockProps } from '@ts-blocks/block-banner-text-block/interfaces'
// hooks
import { useBlockBannerTextBlock } from '@hooks/blocks/block-banner-text-block.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// components
import { Button } from '@brighterly/lib-lumee-ui'
import { AppImage } from '@UI/Images/AppImage'
import { AppTextMarkdown } from '@UI/Typography/AppTextMarkdown'

defineOptions({
  name: 'BlockBannerTextBlock',
})

const props = defineProps<{
  block: BlockBannerTextBlockProps
}>()

const {
  refPrimaryCta,
  refSecondaryCta,
  refShowActions,
  handleClickPrimary,
  handleClickSecondary,
} = useBlockBannerTextBlock({
  block: () => props.block,
})
</script>

<template>
  <article class="block-banner-text-block">
    <div class="block-banner-text-block__content">
      <div class="block-banner-text-block__text">
        <h3 class="block-banner-text-block__title btly-text-heading-s md:btly-text-heading-m lg:btly-text-heading-l">
          {{ block.title }}
        </h3>
        <AppTextMarkdown
          class="block-banner-text-block__richtext"
          :content="block.content"
        />
      </div>
      <div
        v-if="refShowActions"
        class="block-banner-text-block__actions"
      >
        <a
          v-if="refPrimaryCta.show"
          class="block-banner-text-block__cta"
          :href="refPrimaryCta.href"
          @click="handleClickPrimary"
        >
          <Button
            view="primary"
            size="md"
            :text="refPrimaryCta.text"
            tabindex="-1"
          />
        </a>
        <a
          v-if="refSecondaryCta.show"
          class="block-banner-text-block__cta"
          :href="refSecondaryCta.href"
          @click="handleClickSecondary"
        >
          <Button
            view="secondary"
            size="md"
            :text="refSecondaryCta.text"
            tabindex="-1"
          />
        </a>
      </div>
    </div>
    <div class="block-banner-text-block__media">
      <div class="block-banner-text-block__image-slot">
        <AppImage
          v-if="block.image"
          class="block-banner-text-block__image"
          :media="block.image"
          :sizes="STRAPI_IMAGE_SIZES"
        />
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss" src="@scss/components/Blocks/BlockBannerTextBlock.scss" />
